import React, { useState, useEffect, lazy, Suspense } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faHandshake, faPersonCirclePlus } from '@fortawesome/free-solid-svg-icons';
import SEOHelmet from '../Component/SEOHelmet';
import Client from '../Component/Home/Client';
import NoDataFull from './NoDataFull';

const Header = lazy(() => import('../Component/Header'));
const Footer = lazy(() => import('../Component/Footer'));
const Portfolio = lazy(() => import('../Component/Home/Portfolio'));

const Home = () => {
    const [loading, setLoading] = useState(true);
    const [portfolio, setPortfolio] = useState(false);
    const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState("product");
    const [apiError, setApiError] = useState(false);

    const handleClick = () => {
        window.location.href = '/hiring';
    };

    useEffect(() => {
        // Retrieve data from sessionStorage
        const storedData = sessionStorage.getItem('hiringStatus');
        if (storedData) {
            setData(JSON.parse(storedData)); // Parse the JSON string
        }
    }, []);

    const getData = () => {
        setLoading(true);
        setApiError(false);
        
        axios.get('https://api.ps-link.world/api/plexus/portfolio/read')
            .then((res) => {
                setPortfolio(res.data.data);
                setLoading(false);
                setApiError(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Failed to fetch data.");
                setLoading(false);
                setApiError(true); // Set error state when API fails
            });
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Check if portfolio has data
    const hasPortfolioData = () => {
        if (!portfolio) return false;
        if (Array.isArray(portfolio)) return portfolio.length > 0;
        if (typeof portfolio === 'object') return Object.keys(portfolio).length > 0;
        return true;
    };

    // Function to render the appropriate component based on active tab
    const renderTabContent = () => {
        switch (activeTab) {
            case "product":
                return <Portfolio portfolio={portfolio} loading={loading} />;
            case "client":
                return <Client portfolio={portfolio} loading={loading} />;
            default:
                return <Portfolio portfolio={portfolio} loading={loading} />;
        }
    };

    useEffect(() => {
        getData();

        const handleScroll = () => {
            if (window.scrollY > 200) {
                setIsScrollTopVisible(true);
            } else {
                setIsScrollTopVisible(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);


    // Show NoDataFull only after loading is complete and either API failed or no data exists
    if (!loading && (apiError || !hasPortfolioData())) {
        return <NoDataFull />;
    }

    return (
        <>
            <SEOHelmet
                title="Portfolio | Plexus Technology"
                description="Explore our portfolio to see how Plexus Technology delivers innovative IT solutions and transforms businesses. Discover successful projects across various industries, showcasing our expertise and impact."
                keywords="Case Studies , Innovation Portfolio , IT Solutions , App Development , Mobile Apps , Gaming Portfolio , Plexus Apps , Entertainment Apps , Gaming Projects , IT Portfolio , Consulting Portfolio , Software Projects , Tech Portfolio , AI Projects , Cybersecurity Solutions , Tech Innovation , Mobile Gaming , Development Services , Machine Learning , Software Solutions , App Solutions , Technology Development"
                ogTitle="Portfolio | Plexus Technology"
                ogDescription="Explore our portfolio to see how Plexus Technology delivers innovative IT solutions and transforms businesses. Discover successful projects across various industries, showcasing our expertise and impact."
                ogImage="https://plexus-technology.in/static/media/logo.589291ad7a3760919228.png"
                twitterCard="summary_large_image"
            />
            <Suspense>
                <Header />

                {/* main component */}
                <div style={{ background: "#f2f2f2" }}>
                    <div className="portfolio-bg mt-1 d-flex align-items-center justify-content-center" style={{ height: "280px" }}>
                        <h1 className="hero-title w-100 h-100 m-0 text-center text-white" style={{ background: 'rgba(0, 0, 0, 0.20)', lineHeight: "280px", color: "#132028", fontWeight: "600" }}>Portfolio</h1>
                    </div>
                    <div className="container pt-5">
                        <div className="row justify-content-center">
                            <div className="col-md-11">
                                <div className="d-flex justify-content-center bg-white rounded-pill p-2 gap-3">
                                    <button
                                        className={`flex-fill btn rounded-pill fw-medium fs-6 py-2 px-4 transition-all 
            ${activeTab === "product" ? "btn-primary text-white bg-blue shadow-sm" : "btn-white text-black border-0"}`}
                                        onClick={() => setActiveTab("product")}
                                    >
                                        Our Product
                                    </button>
                                    <button
                                        className={`flex-fill btn rounded-pill fw-medium fs-6 py-2 px-4 transition-all 
            ${activeTab === "client" ? "btn-primary text-white bg-blue shadow-sm" : "btn-white text-black border-0"}`}
                                        onClick={() => setActiveTab("client")}
                                    >
                                        Client Work
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Render tab content with proper loading/no-data handling */}
                    {renderTabContent()}

                    <Footer />
                </div>
            </Suspense>

            <>
                <div className="fixed-button d-flex">
                    <div className="w-auto mx-auto d-flex gap-3 flex-wrap align-items-center justify-content-center">
                        {data === "true" && (
                            <button
                                className="button1"
                                onClick={handleClick}
                            >
                                <FontAwesomeIcon icon={faPersonCirclePlus} className="me-2" />
                                We're Hiring
                            </button>
                        )}

                        <a
                            href="/portfolio"
                            className="button2 text-decoration-none"
                        >
                            <FontAwesomeIcon icon={faHandshake} className="me-2" />
                            Let’s Collaborate
                        </a>
                    </div>
                </div>
            </>

            {isScrollTopVisible && (
                <p className='fixed-top-btn cursor' onClick={scrollToTop}>
                    <FontAwesomeIcon icon={faArrowUp} className='px-2' />
                </p>
            )}
        </>
    );
};

export default Home;